<script setup lang="ts">
/**
 * One grey block, standing in for something still loading.
 *
 * **Shape is a prop, not a class.** A caller passing `rounded-full` would land
 * it on this same root beside the `rounded-*` set here, and which one won
 * would depend on the order Tailwind happened to emit them in — the same
 * attribute-fallthrough trap that made `Avatar` stretch and the left rail's
 * gap vanish. Callers size these with `w-*`/`h-*`, which collide with nothing.
 *
 * `aria-hidden`, and never given text: a screen reader should hear the
 * loading status once, from the region that owns it, not a dozen times from
 * the blocks inside it.
 *
 * The pulse stops on its own for anybody who asked for less motion — main.css
 * clamps every animation under `prefers-reduced-motion`, leaving a plain grey
 * block, which is still a truthful placeholder.
 */
withDefaults(defineProps<{ shape?: 'line' | 'circle' | 'block' }>(), { shape: 'line' });

const SHAPES = {
  line: 'rounded',
  circle: 'rounded-full',
  block: 'rounded-xl',
} as const;
</script>

<template>
  <span
    class="block bg-surface-container-high"
    :class="SHAPES[shape]"
    aria-hidden="true"
  />
</template>
